import { Box } from '@mui/material';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomTextarea } from '../common/CustomTextarea';
import { CommonIcon } from '../common/CommonIcon';

interface JournalTextInputProps {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
    placeholder?: string;
    onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
    onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
}

export interface JournalTextInputRef {
    scrollToBottom: () => void;
}

const SCROLL_THRESHOLD = 8;

export const JournalTextInput = forwardRef<JournalTextInputRef, JournalTextInputProps>(
    ({ value, onChange, placeholder, onFocus, onBlur }, ref) => {
    const { t } = useTranslation();
    const [hasMoreBelow, setHasMoreBelow] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    const checkScroll = useCallback((el: HTMLTextAreaElement | null) => {
        if (!el) return;
        const { scrollTop, clientHeight, scrollHeight } = el;
        setHasMoreBelow(scrollHeight - scrollTop - clientHeight > SCROLL_THRESHOLD);
    }, []);

    const handleScroll = useCallback(
        (e: React.UIEvent<HTMLTextAreaElement>) => {
            checkScroll(e.currentTarget);
        },
        [checkScroll]
    );

    const setRef = useCallback((el: HTMLTextAreaElement | null) => {
        textareaRef.current = el;
        checkScroll(el);
    }, [checkScroll]);

    const scrollToBottom = useCallback(() => {
        const el = textareaRef.current;
        if (el) {
            el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
            setTimeout(() => checkScroll(el), 400);
        }
    }, [checkScroll]);

    useImperativeHandle(ref, () => ({ scrollToBottom }), [scrollToBottom]);

    useEffect(() => {
        const id = requestAnimationFrame(() => {
            requestAnimationFrame(() => checkScroll(textareaRef.current));
        });
        return () => cancelAnimationFrame(id);
    }, [value, checkScroll]);

    return (
        <Box sx={{ paddingTop: '16px', flex: 1, minHeight: 0, display: 'flex', position: 'relative' }}>
            <CustomTextarea
                ref={setRef}
                value={value}
                onChange={onChange}
                onFocus={onFocus}
                onBlur={onBlur}
                onScroll={handleScroll}
                placeholder={placeholder || t('journal.placeholder')}
                wrapperStyle={{ flex: 1, minHeight: 200 }}
                style={{
                    minHeight: 200,
                    height: '100%',
                    width: '100%',
                    fontSize: '16px',
                    lineHeight: 1.6,
                }}
            />
            {hasMoreBelow && (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 8,
                        left: 0,
                        right: 0,
                        display: 'flex',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                    }}
                >
                    <CommonIcon
                        icon="expand_more"
                        type="round"
                        size={28}
                        color="var(--tg-theme-hint-color)"
                    />
                </Box>
            )}
        </Box>
    );
});
